/* ============================================================================
 * File: assets/components/predictive-search-hero.js
 * Purpose: Hero dropdown predictive search with auto-reopen on focus/back nav
 * ========================================================================== */

const debounce = (fn, ms = 300) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

class PredictiveSearchHero extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsWrap = this.querySelector('[data-results]'); // .hero__search-results
    this.form = this.querySelector('form');
    this.limit = parseInt(this.getAttribute('data-limit') || '5', 10);
    this.abortController = null;

    this.sectionEl = this.closest('.shopify-section');

    this.activeIndex = -1;
    this.items = [];
    this.list = null;

    this._onTransitionEnd = this._onTransitionEnd.bind(this);
    this._onPageShow = this._onPageShow.bind(this);
    this._maybeReopenFromValue = this._maybeReopenFromValue.bind(this);
  }

  connectedCallback() {
    if (!this.input || !this.resultsWrap) return;

    // typing → fetch
    this.input.addEventListener('input', debounce(() => this.onChange()));

    // keyboard nav
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));

    // click outside → close
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });

    // reopen if value exists when user focuses or taps the input
    this.input.addEventListener('focus', this._maybeReopenFromValue);
    this.input.addEventListener('pointerup', this._maybeReopenFromValue); // why: iOS tap won't fire input event

    // reopen on history back/forward (BFCache) if value exists
    window.addEventListener('pageshow', this._onPageShow);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.removeEventListener('pageshow', this._onPageShow);
  }

  _onPageShow(e) {
    // why: only act on back/forward restores or BFCache restores
    const nav = performance.getEntriesByType?.('navigation')?.[0];
    const isBF = e?.persisted || (nav && nav.type === 'back_forward');
    if (!isBF) return;
    this._maybeReopenFromValue();
  }

  _maybeReopenFromValue() {
    const q = (this.input.value || '').trim();
    if (!q) return;                // nothing typed
    if (this.resultsWrap.innerHTML) { // stale content: just open (animation handles visibility)
      this.open();
      return;
    }
    this.fetchResults(q);          // refetch fresh suggestions
  }

  onChange() {
    const q = (this.input.value || '').trim();
    if (!q) { this.close(); return; }
    this.fetchResults(q);
  }

  fetchResults(q) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q,
      'resources[limit]': String(this.limit),
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last'
    });

    fetch(`${window.Shopify.routes.root}search/suggest?${params}&section_id=search-suggest-hero`, { signal: this.abortController.signal })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.text(); })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-search-suggest-hero');
        this.resultsWrap.innerHTML = section ? section.innerHTML : '';
        this.bindList();
        this.open();
        this.highlightTerm(q);
      })
      .catch(() => { /* silent */ });
  }

  bindList() {
    this.list = this.resultsWrap.querySelector('#predictive-search-hero-list');
    this.items = this.list ? Array.from(this.list.querySelectorAll('.predictive-hero__item')) : [];
    this.activeIndex = -1;

    this.items.forEach((li, idx) => {
      li.addEventListener('mousemove', () => this.setActive(idx));
      li.addEventListener('click', (e) => {
        const link = li.querySelector('a');
        if (link) { e.preventDefault(); window.location.assign(link.href); }
      });
    });
  }

  highlightTerm(term) {
    if (!term) return;
    const rx = new RegExp(`(${escapeRegExp(term)})`, 'ig');
    this.resultsWrap.querySelectorAll('.predictive-hero__title').forEach(el => {
      const raw = el.getAttribute('data-title') || el.textContent || '';
      el.innerHTML = raw.replace(rx, '<mark>$1</mark>');
    });
  }

  onKeydown(e) {
    if (!this.items || this.items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); this.move(1); break;
      case 'ArrowUp':   e.preventDefault(); this.move(-1); break;
      case 'Enter':
        if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
          e.preventDefault();
          const link = this.items[this.activeIndex].querySelector('a');
          if (link) window.location.assign(link.href);
        } else if (this.form) {
          this.form.submit();
        }
        break;
      case 'Escape': this.close(); break;
      default: break;
    }
  }

  move(delta) {
    const maxIndex = this.items.length - 1;
    if (maxIndex < 0) return;
    let next = (this.activeIndex + delta + this.items.length) % this.items.length;
    this.setActive(next);
  }

  setActive(idx) {
    if (!this.items || !this.items.length) return;

    if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
      this.items[this.activeIndex].classList.remove('is-active');
      this.items[this.activeIndex].setAttribute('aria-selected', 'false');
    }

    this.activeIndex = idx;
    const el = this.items[this.activeIndex];
    if (!el) return;

    el.classList.add('is-active');
    el.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', el.id || '');

    this.ensureVisibleSmooth(this.resultsWrap, el, 8, 180);
  }

  // Smoothly scroll container so el is fully visible (padding px, duration ms)
  ensureVisibleSmooth(container, el, pad = 8, duration = 180) {
    if (!container || !el) return;
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();

    let target = null;
    if (eRect.top < cRect.top) target = container.scrollTop + (eRect.top - cRect.top) - pad;
    else if (eRect.bottom > cRect.bottom) target = container.scrollTop + (eRect.bottom - cRect.bottom) + pad;

    if (target == null) return;

    if ('scrollBehavior' in document.documentElement.style &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      container.scrollTo({ top: target, behavior: 'smooth' });
      return;
    }

    const start = container.scrollTop, change = target - start, t0 = performance.now();
    const ease = t => 0.5 - Math.cos(Math.PI * t) / 2;
    const step = now => {
      const t = Math.min(1, (now - t0) / duration);
      container.scrollTop = start + change * ease(t);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  open() {
    this.resultsWrap.style.display = 'block';
    requestAnimationFrame(() => this.resultsWrap.classList.add('is-open')); // CSS anim
    if (this.sectionEl) this.sectionEl.classList.add('hero-search-open');   // raise z-index
  }

  close() {
    this.resultsWrap.classList.remove('is-open');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) this._finalizeClose();
    else {
      this.resultsWrap.removeEventListener('transitionend', this._onTransitionEnd);
      this.resultsWrap.addEventListener('transitionend', this._onTransitionEnd, { once: true });
    }

    this.resultsWrap.innerHTML = '';
    this.items = [];
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    if (this.sectionEl) this.sectionEl.classList.remove('hero-search-open');
  }

  _onTransitionEnd(e) {
    if (e.propertyName === 'opacity' || e.propertyName === 'transform') this._finalizeClose();
  }

  _finalizeClose() {
    this.resultsWrap.style.display = 'none';
    this.resultsWrap.removeEventListener('transitionend', this._onTransitionEnd);
  }
}

function escapeRegExp(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

if (!customElements.get('predictive-search-hero')) {
  customElements.define('predictive-search-hero', PredictiveSearchHero);
}
